<template>
  <BaseDialog max-width="900" persistent :value="true">
    <template #modal>
      <BaseCosmoCard>
        <template #title>
          <div class="modal_header">
            <BaseTitle v-text="$t('automation.effect.send_signature')" />
            <BaseButtonIcon
              class="close_icon"
              icon="$mdiClose"
              :disabled="isSubmiting"
              @click="reset()"
            />
          </div>
        </template>
        <div class="modal_content">
          <v-progress-linear
            v-if="isSubmiting"
            class="progression"
            indeterminate
          />
          <!-- ACTION SECTION -->
          <div class="input_group">
            <div class="input_section">
              <template v-if="isInitializing">
                <v-skeleton-loader class="notification_skeleton" type="text" />
              </template>
              <template v-else>
                <div class="d-flex flex-column">
                  <v-tabs v-model="tab" grow>
                    <v-tab>
                      Configuration
                      <base-icon
                        v-show="$v.effect.$dirty"
                        medium
                        :icon="$v.effect.$invalid ? '$mdiClose' : '$mdiCheck'"
                        :color="$v.effect.$invalid ? 'error' : 'success'"
                        class="ml-2"
                    /></v-tab>
                    <v-tab :disabled="$v.effect.$invalid">
                      {{ $tc("utils.recipient", 2) }}
                      <base-icon
                        v-show="$v.signatories.$dirty"
                        medium
                        :icon="
                          $v.signatories.$invalid ? '$mdiClose' : '$mdiCheck'
                        "
                        :color="$v.signatories.$invalid ? 'error' : 'success'"
                        class="ml-2"
                    /></v-tab>
                    <v-tab :disabled="$v.signatories.$invalid">
                      {{ $t("folder.signaturePosition") }}
                      <base-icon
                        v-show="$v.filesWithSignaturePosition.$dirty"
                        medium
                        :icon="
                          $v.filesWithSignaturePosition.$invalid
                            ? '$mdiClose'
                            : '$mdiCheck'
                        "
                        :color="
                          $v.filesWithSignaturePosition.$invalid
                            ? 'error'
                            : 'success'
                        "
                        class="ml-2"
                      />
                    </v-tab>
                  </v-tabs>

                  <v-tabs-items v-model="tab">
                    <v-tab-item>
                      <!-- first tab -->
                      <div class="action_section">
                        <!-- template first item -->
                        <div class="d-flex align-items-center">
                          <div class="offer_item">
                            <div
                              class="input_label"
                              v-text="$t('utils.template')"
                            />
                            <base-autocomplete
                              v-model="effect.content_id"
                              :items="offerTemplates.items"
                              outlined
                              item-text="title"
                              item-value="id"
                              :disabled="
                                isSubmiting || !!automationActionToEdit
                              "
                              :error-messages="effectContentError"
                              :has-error="!!effectContentError"
                              :loading="fetchingOfferTemplate"
                              clearable
                              @change="fetchTemplateDetails"
                            />
                          </div>
                          <div class="d-flex align-center">
                            <input
                              id="sequential"
                              v-model="offerSequential"
                              type="checkbox"
                              class="mx-2"
                            />
                            <label for="sequential">
                              {{ $t("folder.signatureMode") }}</label
                            >
                          </div>
                        </div>
                        <!--  -->
                        <!-- LABEL SECTION -->
                        <div class="advanced_content">
                          <div class="input_label" v-text="$t('utils.label')" />
                          <div class="input_section">
                            <base-text-field
                              v-model="effect.label"
                              :placeholder="
                                $t('automation.dialog.label_action')
                              "
                              outlined
                            />
                          </div>
                        </div>
                        <!-- ADVANCED SETTING -->
                        <div class="advanced_content">
                          <!-- LOCATION -->
                          <div class="advanced_input">
                            <div
                              class="input_label"
                              v-text="$tc('utils.status', 2)"
                            />
                            <div class="input_section">
                              <base-autocomplete
                                v-model="statuses"
                                :items="workflowStatuses"
                                :placeholder="
                                  $t('automation.dialog.choose_status')
                                "
                                outlined
                                item-text="name"
                                item-value="id"
                                :disabled="isSubmiting"
                                multiple
                                clearable
                              />
                            </div>
                          </div>
                          <div class="advanced_input">
                            <div
                              class="input_label"
                              v-text="$t('utils.destination')"
                            />
                            <div class="input_section">
                              <base-autocomplete
                                v-model="questionDestination"
                                :loading="wfQuestions.loading"
                                :items="wfQuestions.items"
                                :filter="() => true"
                                :search-input.sync="wfQuestions.name"
                                :placeholder="
                                  $t('utils.chooseFieldDestination')
                                "
                                item-text="name"
                                item-value="slug"
                                clearable
                                @change="alertUser"
                              >
                                <template #append-item>
                                  <div
                                    v-intersect="loadMoreWorkflowQuestions"
                                  />
                                </template>
                              </base-autocomplete>
                            </div>
                          </div>
                          <!-- SINGLE TRIGGER -->
                          <div class="checkbox_item">
                            <label
                              for="unique"
                              v-text="$t('utils.single_trigger')"
                            />
                            <input
                              id="unique"
                              v-model="unique"
                              type="checkbox"
                              name="unique"
                              class="ml-2"
                            />
                          </div>
                        </div>
                      </div>
                      <!--  -->
                    </v-tab-item>

                    <v-tab-item>
                      <div class="signature-content">
                        <draggable
                          :list="signatories"
                          :disabled="signatories.length < 2"
                        >
                          <div
                            v-for="(signatory, index) in $v.signatories.$each
                              .$iter"
                            :key="`${index}+${signatory.$model.email}`"
                            class="destinataire-item"
                            :class="{ disabled: signatories.length < 2 }"
                          >
                            <div class="destinataire-header">
                              <div
                                class="header-title d-flex justify-space-between align-center"
                              >
                                <span
                                  v-text="
                                    `${$tc('utils.signatory', 2)} ${+index + 1}`
                                  "
                                />
                                <div class="action">
                                  <div
                                    v-if="signatories.length > 1"
                                    class="button"
                                  >
                                    <BaseButtonIcon
                                      color="secondary"
                                      small
                                      icon="$mdiTrashCanOutline"
                                      @click.prevent="removeSignatory(index)"
                                    />
                                  </div>
                                </div>
                              </div>
                              <template
                                v-if="signatory.$model.type === 'CONTACT'"
                              >
                                <div class="contact_signatory">
                                  Le contact signe ici
                                </div>
                              </template>
                              <template v-else>
                                <div class="destinataire-contact">
                                  <div class="left-item">
                                    <div class="item">
                                      <v-combobox
                                        v-model="signatories[index]"
                                        class="contact"
                                        outlined
                                        dense
                                        flat
                                        :items="suggestedSignatories"
                                        :filter="filterSignatory"
                                        :loading="fetchingsSuggestedSignatories"
                                        :menu-props="{
                                          bottom: true,
                                          offsetY: true,
                                        }"
                                        :label="`${$tc('utils.email', 2)}*`"
                                        item-text="email"
                                        item-value="uniqId"
                                        :error-messages="
                                          signatoryEmailErrors[index]
                                        "
                                        :has-error="
                                          signatoryEmailErrors[index].length > 0
                                        "
                                        @change="
                                          setSignatoryDefaultValue(index)
                                        "
                                      >
                                        <template #item="{ item }">
                                          <div
                                            v-if="item"
                                            class="signatory_item"
                                          >
                                            <BaseIcon
                                              class="icon"
                                              :icon="item.icon"
                                            />
                                            <div
                                              class="email"
                                              v-text="item.email"
                                            />
                                            <div
                                              v-if="
                                                item.lastName || item.firstName
                                              "
                                              class="separator"
                                              v-text="'-'"
                                            />
                                            <div
                                              v-if="item.lastName"
                                              class="name"
                                              v-text="item.lastName"
                                            />
                                            <div
                                              v-if="item.firstName"
                                              class="firstname"
                                              v-text="item.firstName"
                                            />
                                          </div>
                                        </template>
                                      </v-combobox>
                                      <BaseTextField
                                        v-model="signatories[index].phoneNumber"
                                        dense
                                        :label="`${$tc(
                                          'utils.phoneNumber',
                                          3
                                        )}*`"
                                        placeholder="0612345678"
                                        :error-messages="
                                          signatoryPhoneErrors &&
                                          signatoryPhoneErrors[index]
                                        "
                                        :has-error="
                                          signatoryPhoneErrors &&
                                          signatoryPhoneErrors[index].length > 0
                                        "
                                        class="contact"
                                        @change="$v.signatories.$touch()"
                                      />
                                    </div>

                                    <div class="item item1">
                                      <BaseTextField
                                        v-model="signatories[index].firstName"
                                        dense
                                        :label="`${$t('utils.firstName')}*`"
                                        :error-messages="
                                          signatoryFirstNameErrors &&
                                          signatoryFirstNameErrors[index]
                                        "
                                        :has-error="
                                          signatoryFirstNameErrors &&
                                          signatoryFirstNameErrors[index]
                                            .length > 0
                                        "
                                        class="contact"
                                        @change="$v.signatories.$touch()"
                                      />
                                      <BaseTextField
                                        v-model="signatories[index].lastName"
                                        dense
                                        :label="`${$t('utils.lastName')}*`"
                                        :error-messages="
                                          signatoryLastNameErrors &&
                                          signatoryLastNameErrors[index]
                                        "
                                        :has-error="
                                          signatoryLastNameErrors &&
                                          signatoryLastNameErrors[index]
                                            .length > 0
                                        "
                                        class="contact"
                                        @change="$v.signatories.$touch()"
                                      />
                                    </div>
                                  </div>
                                </div>
                              </template>
                            </div>
                          </div>
                        </draggable>

                        <div class="destinataire_actions">
                          <div class="add-signatory" @click="addSignatory">
                            <base-icon icon="$mdiPlus" small />
                            <span v-text="$t('utils.addSignatory')" />
                          </div>
                          <div
                            v-if="
                              !signatories.some(
                                (signatory) => signatory.type === 'CONTACT'
                              )
                            "
                            class="add-signatory"
                            @click="addContactAsSignatory"
                          >
                            <span v-text="$t('utils.contactSignHere')" />
                          </div>
                        </div>
                      </div>
                    </v-tab-item>
                    <v-tab-item>
                      <div class="signature-content pdf_container">
                        <base-pdf-signature-positioner
                          v-if="tab == 2"
                          :files="offerTemplateFiles"
                          :page-signature="offerTemplatePage"
                          :signatories="signatories"
                          is-from-effect
                          :is-error="$v.filesWithSignaturePosition.$anyError"
                          @all-file-validated="filePositionValidate"
                          @update-position="updateFilePositionSignature"
                        />
                      </div>
                    </v-tab-item>
                  </v-tabs-items>
                </div>
              </template>
            </div>
          </div>
        </div>

        <div class="modal_footer">
          <BaseButton
            :disabled="isSubmiting"
            color="grey"
            type="secondary"
            @click="reset()"
          >
            {{ $t("utils.cancel") }}
          </BaseButton>
          <BaseButton
            :disabled="isSubmiting"
            color="primary"
            type="primary"
            @click="submit()"
          >
            {{ $t("utils.validate") }}
          </BaseButton>
        </div>
      </BaseCosmoCard>
    </template>
  </BaseDialog>
</template>

<script>
import { validationMixin } from "vuelidate";
import { required } from "vuelidate/lib/validators";
import WorkflowAPI from "@/modules/Admin/Services/workflow.api";
import { AUTOMATION_EFFECT, AUTOMATION_EVENT } from "@/core/Utils/types.utils";
import OrganizationAPI from "@/core/Services/organization.api";
import { minLength } from "vuelidate/lib/validators";
import { maxLength } from "vuelidate/lib/validators";
import { email } from "vuelidate/lib/validators";
import { Signatory, SIGNATORY_TYPE } from "@/core/Models/Signatory";
import dashboardApi from "@/modules/Authentication/Services/dashboard.api";
import { OfferTemplate } from "@/core/Models/OfferTemplate";
import BasePdfSignaturePositioner from "../../../core/Components/BasePdfSignaturePositioner/BasePdfSignaturePositioner.vue";
import { fileURLToFile } from "@/core/Utils/file.utils";
import draggable from "vuedraggable";
import BaseIcon from "../../../core/Components/UI/Icon/BaseIcon.vue";
import { getWorkflowQuestions } from "../Services/workflow.service";

const notSpecialChar = (value) => {
  return value ? !/[!"#$%&'()*+,./:;<=>?@[\]^_`{|}~]/.test(value) : true;
};
export default {
  name: "EffectSignatureCreateAndEditDialog",
  components: { BasePdfSignaturePositioner, draggable, BaseIcon },
  mixins: [validationMixin],
  props: {
    organizationId: {
      type: [Number, String],
      required: true,
    },
    workflow: {
      type: Object,
      required: true,
    },
    automationActionToEdit: {
      type: Object,
      default: () => null,
    },
  },
  data() {
    return {
      openAdvancedSettings: false,
      isSubmiting: false,
      tab: 0,
      signatories: [
        {
          firstName: null,
          lastName: null,
          email: null,
          phoneNumber: null,
          type: "SIGNATORY",
        },
      ],
      effectItems: [
        {
          label: this.$t("automation.effect.set_status"),
          value: AUTOMATION_EFFECT.SET_STATUS,
        },
        {
          label: this.$t("automation.effect.send_offer"),
          value: AUTOMATION_EFFECT.SEND_OFFER,
        },
        {
          label: this.$t("automation.effect.send_form"),
          value: AUTOMATION_EFFECT.SEND_FORM,
        },
        {
          label: this.$t("automation.effect.send_to_ged"),
          value: AUTOMATION_EFFECT.SEND_TO_GED,
          // disabled: !this.hasEnoxa,
        },
        {
          label: this.$t("automation.effect.archive_folder"),
          value: AUTOMATION_EFFECT.ARCHIVE_FOLDER,
        },
        {
          label: this.$t("automation.effect.send_campaign"),
          value: AUTOMATION_EFFECT.SEND_CAMPAIGN,
        },
        {
          label: this.$t("automation.effect.generate_doc"),
          value: AUTOMATION_EFFECT.GENERATE_DOC,
        },
        {
          label: this.$t("automation.effect.assign_collaborator"),
          value: AUTOMATION_EFFECT.ASSIGN_COLLABORATOR,
        },
      ],
      eventItems: [
        {
          label: this.$t("automation.event_config.manual"),
          value: AUTOMATION_EVENT.MANUAL,
        },
      ],
      effect: {
        label: "",
        action: null,
        content_id: null,
        action_config: null,
      },
      unique: false,
      offerTemplates: {
        items: [],
        count: 0,
        next: 0,
        previous: 0,
      },
      statuses: [],
      questionDestination: null,
      wfQuestions: {
        page: 1,
        pageSize: 10,
        name: undefined,
        items: [],
        loading: false,
      },
      fetchingOfferTemplate: false,
      AUTOMATION_EFFECT: AUTOMATION_EFFECT,
      AUTOMATION_EVENT: AUTOMATION_EVENT,
      isInitializing: true,
      fetchingsSuggestedSignatories: false,
      suggestedSignatories: [],
      offerTemplateDetail: null,
      offerTemplateFiles: [],
      offerTemplatePage: [],
      signatorySectionValidated: false,
      isPositionSignatureValidated: false,
      validateSignatureSection: false,
      filesWithSignaturePosition: [],
      resetPositionSignatureSection: false,
      offerSequential: false,
      positionSignatoryTouched: false,
    };
  },
  validations() {
    let validations = {};

    // effect
    validations.effect = {
      action: {
        required,
      },
      label: {},
      content_id: {
        required,
      },
      action_config: {
        // required,
      },
    };
    // signatories
    validations.signatories = {
      required,
      minLength: minLength(1),
      $each: {
        firstName: {
          required,
          maxLength: maxLength(255),
          notSpecialChar: notSpecialChar,
        },
        lastName: {
          required,
          maxLength: maxLength(255),
          notSpecialChar: notSpecialChar,
        },
        email: {
          required,
          maxLength: maxLength(255),
          email,
          isEmailUnique: (value) => {
            return (
              this.signatories
                .filter((s) => s.email == value)
                .map((sm) => sm.email).length <= 1
            );
          },
        },
        type: {},
        phoneNumber: {
          required,
          phoneNumber: (value) => {
            return /^0+[0-9]{9}$/.test(value);
          },
          nonPhoneReunion: (value) => {
            const temp = value.toString();
            return !(
              temp.startsWith("0692") ||
              temp.startsWith("0693") ||
              temp.startsWith("0792") ||
              temp.startsWith("0793")
            );
          },
        },
      },
    };

    // position signature

    validations.filesWithSignaturePosition = {
      required,
      minLength: minLength(this.offerTemplateFiles.length),
    };

    return validations;
  },
  computed: {
    workflowStatuses() {
      if (!this.workflow?.statuses) return [];
      return this.workflow.statuses;
    },
    effectActionError() {
      if (!this.$v.effect.action.$dirty) return "";
      if (!this.$v.effect.action.required)
        return this.$t("utils.fieldIsRequired");
      return "";
    },
    effectContentError() {
      if (!this.$v.effect.content_id.$dirty) return "";
      if (!this.$v.effect.content_id.required)
        return this.$t("utils.fieldIsRequired");
      return "";
    },
    actionConfigError() {
      if (!this.$v.effect.action_config.$dirty) return "";
      if (!this.$v.effect.action_config.required)
        return this.$t("utils.fieldIsRequired");
      return "";
    },
    signatoryFirstNameErrors() {
      const errors = [];
      for (
        let i = 0;
        i < Object.keys(this.$v.signatories.$each.$iter).length;
        i++
      ) {
        errors.push([]);
        if (!this.$v.signatories.$each.$iter[i.toString()].firstName) return;
        if (!this.$v.signatories.$each.$iter[i.toString()].firstName.$dirty) {
          continue;
        }
        if (!this.$v.signatories.$each.$iter[i.toString()].firstName.required) {
          errors[i].push(this.$t("folder.yousign.errors.lastNameRequired"));
          continue;
        }
        if (
          !this.$v.signatories.$each.$iter[i.toString()].firstName
            .notSpecialChar
        ) {
          errors[i].push(this.$t("folder.yousign.errors.hasSpecialChar"));
          continue;
        }
      }
      return errors;
    },
    signatoryLastNameErrors() {
      const errors = [];
      for (
        let i = 0;
        i < Object.keys(this.$v.signatories.$each.$iter).length;
        i++
      ) {
        errors.push([]);
        if (!this.$v.signatories.$each.$iter[i.toString()].lastName) return;
        if (!this.$v.signatories.$each.$iter[i.toString()].lastName.$dirty) {
          continue;
        }
        if (!this.$v.signatories.$each.$iter[i.toString()].lastName.required) {
          errors[i].push(this.$t("folder.yousign.errors.nameRequired"));
          continue;
        }
        if (
          !this.$v.signatories.$each.$iter[i.toString()].lastName.notSpecialChar
        ) {
          errors[i].push(this.$t("folder.yousign.errors.hasSpecialChar"));
          continue;
        }
      }
      return errors;
    },
    signatoryEmailErrors() {
      const errors = [];
      for (
        let i = 0;
        i < Object.keys(this.$v.signatories.$each.$iter).length;
        i++
      ) {
        errors.push([]);
        if (!this.$v.signatories.$each.$iter[i.toString()].email) continue;
        if (!this.$v.signatories.$each.$iter[i.toString()].email.$dirty) {
          continue;
        }
        if (!this.$v.signatories.$each.$iter[i.toString()].email.required) {
          errors[i].push(this.$t("folder.yousign.errors.emailRequired"));
          continue;
        }
        if (!this.$v.signatories.$each.$iter[i.toString()].email.email) {
          errors[i].push(this.$t("folder.yousign.errors.emailError"));
          continue;
        }
        if (
          !this.$v.signatories.$each.$iter[i.toString()].email.isEmailUnique
        ) {
          errors[i].push(this.$t("email dupliquer"));
          continue;
        }
      }
      return errors;
    },
    signatoryPhoneErrors() {
      const errors = [];
      for (
        let i = 0;
        i < Object.keys(this.$v.signatories.$each.$iter).length;
        i++
      ) {
        errors.push([]);
        if (!this.$v.signatories.$each.$iter[i.toString()].phoneNumber) return;
        if (!this.$v.signatories.$each.$iter[i.toString()].phoneNumber.$dirty) {
          continue;
        }
        if (
          !this.$v.signatories.$each.$iter[i.toString()].phoneNumber.required
        ) {
          errors[i].push(this.$t("folder.yousign.errors.phoneNumberRequired"));
          continue;
        }
        if (
          !this.$v.signatories.$each.$iter[i.toString()].phoneNumber.phoneNumber
        ) {
          errors[i].push(this.$t("folder.yousign.errors.phoneNumberError"));
          continue;
        }
        if (
          !this.$v.signatories.$each.$iter[i.toString()].phoneNumber
            .nonPhoneReunion
        ) {
          errors[i].push(this.$t("folder.yousign.errors.reunionNumber"));
          continue;
        }
      }
      return errors;
    },
  },
  watch: {
    automationActionToEdit: {
      async handler() {
        if (!this.automationActionToEdit) return (this.isInitializing = false);
        await this.buildEventAndActionValue();
      },
      immediate: true,
    },
    "wfQuestions.name"(newVal, oldVal) {
      if (newVal == oldVal || oldVal == null) return;
      this.filterWfQuestions();
    },
    signatories: {
      handler() {
        if (!this.automationActionToEdit) return;
        if (this.filesWithSignaturePosition.length)
          this.$v.filesWithSignaturePosition.$touch();
        this.filesWithSignaturePosition = [];
        return (this.effect.action_config = null);
      },
      deep: true,
    },
  },
  created() {
    this.handleEffectChange();
    this.fetchWorkflowQuestion();
  },
  methods: {
    //destination
    alertUser(item) {
      if (
        this.automationActionToEdit &&
        item !== this.automationActionToEdit?.actionConfig?.destination
      ) {
        return this.$store.dispatch("snackbar/active", {
          message: this.$t("automation.effect.alertMessage"),
          type: "INFO",
        });
      }
    },
    async fetchWorkflowQuestion() {
      this.wfQuestions.loading = true;
      try {
        this.wfQuestions.page = 1;
        const response = await getWorkflowQuestions({
          organizationId: this.$route.params.organizationId,
          workflowId: this.$route.params.workflowId,
          page: this.wfQuestions.page,
          name: this.wfQuestions.name,
          pageSize: this.wfQuestions.pageSize,
          typeQuestion: ["MULTIPLE_FILE"],
          std_only: true,
        });
        this.wfQuestions = { ...this.wfQuestions, ...response };
      } catch {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
      this.wfQuestions.loading = false;
    },
    filterWfQuestions() {
      if (this.wfQuestions.searchTimeoutRef)
        clearTimeout(this.wfQuestions.searchTimeoutRef);
      this.wfQuestions.searchTimeoutRef = setTimeout(async () => {
        this.fetchWorkflowQuestion();
      }, 200);
    },
    async loadMoreWorkflowQuestions(_, __, isIntersecting) {
      if (!isIntersecting) return;
      this.wfQuestions.page = this.wfQuestions.next;
      this.wfQuestions.loading = true;
      try {
        const tmp = await getWorkflowQuestions({
          organizationId: this.$route.params.organizationId,
          workflowId: this.$route.params.workflowId,
          page: this.wfQuestions.page,
          name: this.wfQuestions.name,
          pageSize: this.wfQuestions.pageSize,
          typeQuestion: ["MULTIPLE_FILE"],
          std_only: true,
        });
        this.wfQuestions = {
          ...tmp,
          ...this.wfQuestions,
          items: [...this.wfQuestions.items, ...tmp.items],
        };
      } catch {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
      this.wfQuestions.loading = false;
    },
    // offer signature
    updateFilePositionSignature(fileWithPosition) {
      this.filesWithSignaturePosition.push(fileWithPosition);
    },
    validateSignatorySection() {
      this.$v.signatories.$touch();
      if (this.$v.signatories.$invalid) return;
      this.signatorySectionValidated = true;
    },
    cancelSignatorySection() {
      this.signatorySectionValidated = false;
      this.isPositionSignatureValidated = false;
      this.validateSignatureSection = false;
      this.filesWithSignaturePosition = [];
    },
    async fetchTemplateDetails() {
      this.offerTemplateFiles = [];
      this.offerTemplatePage = [];

      if (!this.effect.content_id) return;
      const response = await OrganizationAPI.getOrganizationOfferTemplateDetail(
        {
          organizationId: this.$route.params.organizationId,
          offerTemplateId: this.effect.content_id,
        }
      );
      this.offerTemplateDetail = new OfferTemplate(response.data);
      for (const file of this.offerTemplateDetail.files) {
        this.offerTemplateFiles.push(
          await fileURLToFile(file.offerFile, file.name, true)
        );
        this.offerTemplatePage.push(file.signaturePage);
      }
      this.$v.effect.$touch();
    },
    async fetchSignatories() {
      this.fetchingsSuggestedSignatories = true;
      try {
        const tmpSignatories = await Promise.all([
          await dashboardApi.getUserSignatories(),
          await dashboardApi.getOrganizationSignatories({
            organizationId: this.$route.params.organizationId,
          }),
        ]);
        this.suggestedSignatories = [
          ...tmpSignatories[0].data.results.map(
            (s) =>
              new Signatory({ ...s, type: SIGNATORY_TYPE.PERSONAL_SIGNATORY })
          ),
          ...tmpSignatories[1].data.results.map(
            (s) =>
              new Signatory({
                ...s,
                type: SIGNATORY_TYPE.ORGANIZATION_SIGNATORY,
              })
          ),
        ];
      } catch {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
      this.fetchingsSuggestedSignatories = false;
      // return [...this.suggestedSignatories];
    },
    filterSignatory(item, queryText) {
      if (!queryText) return true;
      const textOne = item.email.toLowerCase();
      const textTwo = item.firstName ? item.firstName.toLowerCase() : "";
      const textThree = item.lastName ? item.lastName.toLowerCase() : "";
      const searchText = queryText.toLowerCase();
      return (
        textOne.indexOf(searchText) > -1 ||
        textTwo.indexOf(searchText) > -1 ||
        textThree.indexOf(searchText) > -1
      );
    },

    addSignatory() {
      this.signatories.push({
        firstName: null,
        lastName: null,
        email: null,
        phoneNumber: null,
        type: "SIGNATORY",
      });
      if (this.filesWithSignaturePosition.length)
        this.$v.filesWithSignaturePosition.$touch();
      this.filesWithSignaturePosition = [];
      return (this.effect.action_config = null);
    },
    filePositionValidate() {
      this.effect.action_config = {
        version: 1,
        sequential: this.offerSequential,
        files: [...this.filesWithSignaturePosition],
      };
      this.$v.filesWithSignaturePosition.$touch();
    },
    addContactAsSignatory() {
      this.signatories.push({
        firstName: "CONTACT",
        lastName: "DOSSIER",
        email: "defaultcosmocontact@gmail.com",
        phoneNumber: "0612345678",
        type: "CONTACT",
      });
      this.$v.signatories.$touch();
      if (this.filesWithSignaturePosition.length)
        this.$v.filesWithSignaturePosition.$touch();
      this.filesWithSignaturePosition = [];
      return (this.effect.action_config = null);
    },
    setSignatoryDefaultValue(index) {
      if (!this.signatories[index]) return;
      if ("string" != typeof this.signatories[index]) {
        this.signatories[index] = new Signatory({
          ...this.signatories[index],
        });
        return;
      }
      this.signatories[index] = new Signatory({
        email: this.signatories[index],
      });
      this.$v.signatories.$touch();
    },
    removeSignatory(index) {
      this.signatories.splice(index, 1);
      if (this.filesWithSignaturePosition.length)
        this.$v.filesWithSignaturePosition.$touch();
      this.filesWithSignaturePosition = [];
      return (this.effect.action_config = null);
    },

    //
    reset() {
      if (this.isSubmiting) return;
      this.$emit("close");
    },

    async buildEventAndActionValue() {
      this.isInitializing = true;

      // init value
      this.effect = {
        action: this.automationActionToEdit.action,
        label: this.automationActionToEdit.label,
      };
      this.unique = this.automationActionToEdit.unique;
      this.statuses = this.automationActionToEdit.statuses.map(
        (action) => action.id
      );
      // // fetching corresponding value
      await this.handleEffectChange();
      // building content_id
      this.effect.content_id = this.getContentId(
        this.automationActionToEdit.actionExtra
      );

      // build signature effect value
      //   if (
      //     this.automationActionToEdit?.actionConfig?.files?.length &&
      //     this.automationActionToEdit?.actionConfig?.files[0]?.signatures.length
      //   ) {
      //     await this.buildSignatoriesAndSignatureValue();
      //   }
      if (this.automationActionToEdit)
        await this.buildSignatoriesAndSignatureValue();
      this.effect.action_config = this.getActionConfigId(
        this.effect.action,
        this.automationActionToEdit.actionConfig
      );
      this.isInitializing = false;
      this.$v.effect.$touch();
      this.$v.signatories.$touch();
      this.$v.filesWithSignaturePosition.$touch();
    },
    async buildSignatoriesAndSignatureValue() {
      this.signatories =
        this.automationActionToEdit?.actionConfig?.files[0]?.signatures?.map(
          (signature) => {
            if (signature.member === "CONTACT")
              return {
                firstName: "CONTACT",
                lastName: "CONTACT",
                email: "defaultcosmocontact@gmail.com",
                phoneNumber: "0612345678",
                type: "CONTACT",
              };
            else
              return {
                ...signature.member,
                phoneNumber: signature.member?.phoneNumber?.replace("+33", "0"),
                type: "SIGNATORY",
              };
          }
        );
      this.$nextTick(() => {
        this.filesWithSignaturePosition = [
          ...this.automationActionToEdit?.actionConfig?.files,
        ];
        this.questionDestination =
          this.automationActionToEdit?.actionConfig?.destination;
        this.positionSignatoryTouched = true;
      });
      this.validateSignatureSection = true;
      this.signatorySectionValidated = true;

      this.offerSequential =
        this.automationActionToEdit.actionConfig?.sequential;
      await this.fetchTemplateDetails();
    },
    getContentId(info) {
      return [...this.offerTemplates.items].find(
        (offerTemplate) => offerTemplate.title === info.title
      )?.id;
    },
    getActionConfigId(type, config) {
      switch (type) {
        case AUTOMATION_EFFECT.SEND_TO_GED:
          return config.xmlPattern;
        case AUTOMATION_EFFECT.GENERATE_DOC:
          return [...this.questionFiles.items].find(
            (question) => question.slug === config.destination
          )?.slug;
        case AUTOMATION_EFFECT.SEND_OFFER:
          return this.parseConfigFiles({ ...config });
        default:
          return null;
      }
    },
    parseConfigFiles(item) {
      let payload = item.files.map((ite) => {
        return {
          ...ite,
          signatures: ite.signatures.map((sign) => ({
            height: sign.height,
            width: sign.width,
            x: sign.x,
            y: sign.y,
            page_number: sign.pageNumber,
            member:
              sign.member === "CONTACT"
                ? "CONTACT"
                : {
                    email: sign.member.email,
                    first_name: sign.member.firstName,
                    last_name: sign.member.lastName,
                    phone_number: sign.member.phoneNumber,
                  },
          })),
        };
      });
      return { ...item, files: payload };
    },

    async fecthOfferTemplates() {
      this.fetchingOfferTemplate = true;
      const response = await OrganizationAPI.getOrganizationOfferTemplate({
        organizationId: this.organizationId,
        filters: {
          page: 1,
          offer_type: ["SIGNATURE"],
        },
      });
      this.offerTemplates = {
        ...response.data,
        items: response.data.results,
      };
      this.fetchingOfferTemplate = false;
    },
    getPayload() {
      const payload = new FormData();
      payload.append("action", this.effect.action);
      payload.append("action_content_id", this.effect.content_id);
      if (this.effect.label) payload.append("label", this.effect.label);
      payload.append("event", AUTOMATION_EVENT.MANUAL); // always manual
      const actionConfig = {
        ...this.effect.action_config,
        sequential: this.offerSequential,
      };
      if (this.questionDestination)
        actionConfig.destination = this.questionDestination;
      payload.append("action_config", JSON.stringify(actionConfig));
      payload.append("unique", this.unique ? this.unique : false);
      if (this.statuses?.length) {
        this.statuses.forEach((status) => payload.append("statuses", status));
      }
      return payload;
    },
    async postPatchEffect(payload) {
      if (this.automationActionToEdit) {
        await WorkflowAPI.patchOrganizationWorkflowEffet({
          organizationId: this.organizationId,
          workflowId: this.workflow.id,
          effectId: this.automationActionToEdit.id,
          payload: payload,
        });
      } else {
        await WorkflowAPI.postOrganizationWorkflowEffet({
          organizationId: this.organizationId,
          workflowId: this.workflow.id,
          payload: payload,
        });
      }
    },
    async submit() {
      this.$v.effect.$touch();
      this.$v.signatories?.$touch();
      this.$v.filesWithSignaturePosition.$touch();
      if (
        this.$v.effect.$invalid ||
        this.$v.signatories?.$invalid ||
        this.$v.filesWithSignaturePosition.$invalid
      )
        return;
      this.isSubmiting = true;
      try {
        await this.postPatchEffect(this.getPayload());
        this.$store.dispatch("snackbar/active", {
          message: this.$t(
            this.automationActionToEdit
              ? "automation.effect.modify_sucessfully"
              : "automation.effect.created_sucessfully"
          ),
          type: "SUCCESS",
        });
      } catch (error) {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("workflow.snackbar.error.errorModifyingWorkflow"),
          type: "ERROR",
        });
      }
      this.isSubmiting = false;
      this.$emit("effect-created");
      this.reset();
    },
    async handleEffectChange() {
      this.effect.action = AUTOMATION_EFFECT.SEND_OFFER;
      if (!this.suggestedSignatories.length) await this.fetchSignatories();
      if (!this.offerTemplates.items.length) await this.fecthOfferTemplates();
    },
  },
};
</script>

<style lang="scss" scoped>
.modal_header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-grow: 1;
  padding-top: 8px;
  flex-wrap: wrap;
  .modal_title {
    font-weight: 600;
    font-size: 24px;
    line-height: 32px;
    color: #353542;
  }
  .close_icon {
    margin-left: auto;
  }
}
.modal_content {
  padding-top: 24px;
  position: relative;
  max-height: 600px;
  overflow-y: auto;
  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-track {
    background: #fff;
    border-radius: 3px;
    border: 1px solid #eeeef7;
  }

  &::-webkit-scrollbar-thumb {
    background: #b6bdff;
    border-radius: 3px;
  }

  .input_group {
    display: flex;
    gap: 16px;
    .group_label {
      flex: none;
      width: 80px;
      height: 42px;
      display: flex;
      align-items: center;
      font-family: Inter;
      font-size: 16px;
      font-weight: 600;
      line-height: 20px;
      color: #353542;
    }
    .input_section {
      width: 100%;
      flex: none;
      display: flex;
      gap: 16px;
      .action_section {
        display: flex;
        flex-direction: column;
        margin-top: 18px;
        .collaborator_section {
          display: flex;
          flex-direction: column;
          gap: 12px;
          .header {
            width: 100%;
            display: flex;
            .item {
              flex: 1;
              text-decoration: underline;
            }
          }
          .collaborator_content {
            width: 100%;
            display: flex;
            .item {
              flex: 1;
              text-align: center;
            }
          }
        }
        .template-content {
          display: flex;
          gap: 16px;
          .template-container {
            flex: 1;
            .underline {
              text-decoration: underline;
            }
          }
        }
      }
      & > * {
        flex: 1;
      }
      .inputs {
        display: flex;
        gap: 16px;
        & > * {
          width: calc(50% - 8px);
          flex: none;
        }
      }
    }
  }
  .advanced_section {
    .advanced_header {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 16px;
      cursor: pointer;
      .chevron_btn {
        width: 24px;
        flex: none;
        transition: all 0.3s ease-out;
        &.expanded {
          transform: rotate(90deg);
        }
      }
      .header_label {
        flex: none;
        width: 160px;
        align-items: center;
        font-family: Inter;
        font-size: 16px;
        font-weight: 600;
        line-height: 20px;
        color: #353542;
      }
    }
    .advanced_content {
      .advanced_input {
        display: flex;
        .input_label {
          width: 176px;
          height: 42px;
          display: flex;
          align-items: center;
          color: #353542;
          font-family: Inter;
          font-size: 14px;
          line-height: 18px;
          font-style: normal;
          font-weight: 400;
          &.underline {
            text-decoration: underline;
          }
        }
        .input_section {
          width: calc(100% - 176px);
          flex: none;
        }
      }
      .checkbox_item {
        margin-bottom: 16px;
        display: flex;
        align-items: center;
        label {
          width: 176px;
          color: #353542;
          font-family: Inter;
          font-size: 14px;
          line-height: 18px;
          font-style: normal;
          font-weight: 400;
        }
        input {
          width: 18px;
          height: 18px;
          cursor: pointer;
        }
      }
    }
  }
  .progression {
    position: absolute;
    width: 100%;
    top: 0;
    left: 0;
    z-index: 10;
  }
}
.modal_footer {
  display: flex;
  justify-content: flex-end;
  align-content: center;
  padding: 8px 0;
  gap: 16px;
}
.mail_chips {
  background: rgba(155, 189, 255, 0.2) !important;
  color: #2536cc !important;
  border-radius: 21px;
  ::v-deep .v-chip__close {
    color: #2536cc;
  }
}
.notification_skeleton {
  height: 40px;
  width: 100%;
  ::v-deep {
    .v-skeleton-loader__bone {
      height: 40px;
    }
  }
}
::v-deep textarea {
  min-height: 400px;
}

// new

.destinataire-item {
  border-radius: 5px;
  border: 1px dashed #cfcfdc;
  padding: 16px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 24px;
  cursor: grab;
  &.disabled {
    cursor: default;
  }
  .destinataire-header {
    height: 57px;
    padding: 10px;
    width: 100%;
    height: 100%;
    .header-title {
      padding: 10px;
      border-radius: 5px;
      background: var(--principale-gris-fond-de-tableau, #fcfcfc);
    }
    .action {
      gap: 10px;
      .button {
        padding: 8px 16px;
        border-radius: 5px;
        background: #f4f7ff;
        &:disabled {
          opacity: 0.5;
        }
      }
    }
  }
  .destinataire-contact {
    padding: 10px;
    display: flex;
    gap: 24px;
    .left-item {
      flex: 2;
      display: flex;
      flex-direction: column;

      .item {
        display: flex;
        align-items: center;
        gap: 10px;
        .action-type {
          display: flex;
          border-radius: 5px;
          background: #fcfcfc;
          justify-content: center;
          align-items: center;
          padding: 8.75px 16px;
          margin-top: -23px;
          .paragraph {
            color: #707080;
            font-family: Inter;
            font-size: 14px;
            font-style: normal;
            font-weight: 400;
            line-height: normal;
          }
        }
      }
      .item1 {
        margin-top: -6px;
      }
      .mixt {
        width: 527px;
      }
      .action {
        display: flex;
        align-items: center;
        border-radius: 5px;
        background: #fcfcfc;
        justify-content: center;
        align-items: center;
        padding: 8px 16px;
        .paragraph {
          color: #707080;
          font-family: Inter;
          font-size: 14px;
          font-style: normal;
          font-weight: 400;
          line-height: normal;
        }
      }
    }
    .right-item {
      flex: 1;
    }
  }
}
.add-signatory {
  height: 42px;
  margin-bottom: 16px;
  width: 100%;
  border-radius: 5px;
  border: 1px dashed var(--principale-gris-texte, #707080);
  background: #fff;
  padding: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--principale-gris-texte, #707080);
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  gap: 16px;
  cursor: pointer;
  &:hover {
    border-color: #2536cc;
    color: #2536cc;
    ::v-deep svg {
      color: #2536cc !important;
    }
  }
}
.contact_signatory {
  height: 42px;
  border-radius: 5px;
  color: var(--principale-gris-texte, #707080);
  background: lightblue;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 10px;
}
.destinataire_actions {
  padding-top: 16px;
  border-top: 3px solid black;
}
.signatory_item {
  display: flex;
  width: 100%;
  height: 100%;
  justify-content: flex-start;
  align-items: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  gap: 2px;
  color: #323338;
  font-family: "Inter";
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 18px;
  .separator {
    margin: 0;
  }
  .icon {
    ::v-deep {
      .v-icon__svg {
        width: 16px;
        height: 16px;
      }
    }
  }
  .name {
    text-transform: uppercase;
  }
  .firstname {
    text-transform: capitalize;
  }
}
.pdf_container {
  min-height: 400px;
}
.signature-content {
  margin-top: 16px;
}
</style>
